import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, ZoomIn, ZoomOut, Maximize2, Users, GitBranch } from 'lucide-react';
import './OrganizationHierarchyTab.css';
import useOrgHierarchyStore from '@/stores/SuperAdminStore/OrgHierarchyStore';
import { HierarchyNode } from '@/services/PagesServices/OrgHierarchyService';
import OrgChartNode from './OrgChartNode';
import ReassignManagerModal from './ReassignManagerModal';

/**
 * Super Admin — Organization Hierarchy (tab of the Organizations page).
 * Reporting-line org chart across the platform. Global/standalone — it is not
 * scoped to a selected organization, so it fetches its own tree and needs no
 * org picker. The page chrome (breadcrumb + header) comes from Organizations.tsx;
 * the two people/top-level counts live in this tab's own toolbar.
 */
const OrganizationHierarchyTab: React.FC = () => {
  const { tree, totalUsers, rootCount, loadingTree, fetchTree, searchResults, search, clearSearch } =
    useOrgHierarchyStore();

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [highlightUuid, setHighlightUuid] = useState<string | null>(null);
  const [reassignNode, setReassignNode] = useState<HierarchyNode | null>(null);

  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Zoom / pan
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const panState = useRef<{ dragging: boolean; startX: number; startY: number; ox: number; oy: number }>({
    dragging: false, startX: 0, startY: 0, ox: 0, oy: 0,
  });

  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => { search(query); }, 250);
    return () => clearTimeout(t);
  }, [query, search]);

  const toggle = useCallback((uuid: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
      return next;
    });
  }, []);

  const jumpTo = (uuid: string) => {
    setHighlightUuid(uuid);
    setShowResults(false);
    setQuery('');
    clearSearch();
    // Wait a tick for any re-render, then scroll the node into view.
    setTimeout(() => {
      const el = document.getElementById(`org-node-${uuid}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }, 60);
  };

  // Pan handlers
  const onMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.s-admin-org-node')) return; // don't pan when grabbing a node button
    panState.current = { dragging: true, startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!panState.current.dragging) return;
    setOffset({
      x: panState.current.ox + (e.clientX - panState.current.startX),
      y: panState.current.oy + (e.clientY - panState.current.startY),
    });
  };
  const endPan = () => { panState.current.dragging = false; };

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return; // require modifier so normal scroll still works
    e.preventDefault();
    setScale((s) => Math.min(2, Math.max(0.4, s - e.deltaY * 0.001)));
  };

  const resetView = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  return (
    <div className="s-admin-org">
      <div className="s-admin-org-toolbar">
        <div className="s-admin-org-searchbar">
          <Search size={16} />
          <input
            placeholder="Find a person and jump to them"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
          />
          {showResults && searchResults.length > 0 && (
            <ul className="s-admin-org-searchdrop">
              {searchResults.map((hit) => (
                <li key={hit.uuid} onClick={() => jumpTo(hit.uuid)}>
                  <strong>{hit.name}</strong>
                  <span>{hit.email} · {hit.roleLabel}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="s-admin-org-stats">
          <span><Users size={15} /> {totalUsers} people</span>
          <span><GitBranch size={15} /> {rootCount} top-level</span>
        </div>
        <div className="s-admin-org-zoom">
          <button onClick={() => setScale((s) => Math.max(0.4, s - 0.1))} title="Zoom out"><ZoomOut size={16} /></button>
          <span>{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => Math.min(2, s + 0.1))} title="Zoom in"><ZoomIn size={16} /></button>
          <button onClick={resetView} title="Reset view"><Maximize2 size={16} /></button>
        </div>
      </div>

      <div
        className="s-admin-org-canvas"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        onWheel={onWheel}
      >
        {loadingTree ? (
          <div className="s-admin-org-empty">Loading hierarchy…</div>
        ) : tree.length === 0 ? (
          <div className="s-admin-org-empty">No active users to display.</div>
        ) : (
          <div
            className="s-admin-org-stage"
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          >
            <div className="s-admin-org-tree">
              <ul className="s-admin-org-roots">
                {tree.map((root) => (
                  <OrgChartNode
                    key={root.uuid}
                    node={root}
                    highlightUuid={highlightUuid}
                    collapsed={collapsed}
                    onToggle={toggle}
                    onReassign={setReassignNode}
                  />
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {reassignNode && (
        <ReassignManagerModal node={reassignNode} onClose={() => setReassignNode(null)} />
      )}
    </div>
  );
};

export default OrganizationHierarchyTab;
